import {accountsByPk} from './wizardPayload';

/**
 * Cuánto del cupo de cuentas del plan usa la cartera que el usuario armó.
 *
 * Existe porque el tope lo descubría el backend recién al vincular, cuenta por cuenta: el 24/09 una
 * agencia en prueba eligió 102 cuentas sobre un cupo de 70 y el alta entera se cayó. El túnel tiene
 * que decirlo ANTES de dejar postear, con el mismo criterio que el backend hace cumplir: se cuentan
 * los vínculos cliente↔cuenta NUEVOS. Una cuenta que ya está vinculada al cliente al que va no suma.
 *
 * `quota` es `plan_quota` de la propuesta del paso 3 (`{limit, used, available}`); sin él —agencia
 * sin suscripción legible, o un backend viejo— devuelve `null` y la pantalla no bloquea nada: el
 * backend igual rechaza antes de escribir, así que el peor caso es un error claro, no uno silencioso.
 *
 * Es una estimación de la pantalla y puede quedar corta (una cuenta que el backend saltea por estar
 * cubierta la cuenta igual); nunca se usa para decidir sola: el backend es el que decide.
 *
 * `clients` son los clientes que esa cartera va a CREAR —uno por cliente nuevo con alguna cuenta
 * elegida— y `left` lo que queda del cupo después. Viajan juntos porque la nota los dice juntos
 * («Vas a crear N clientes con M cuentas…»): hasta el 30/09 decía «usa 7 de las 70 cuentas» al lado
 * de un botón que contaba CLIENTES, y Leo leyó dos unidades distintas como el mismo número (card
 * 2yINyKQv).
 *
 * Sólo los NUEVOS: la frase dice «crear», y un cliente que ya existe no se crea. Contarlos es el mismo
 * defecto que el botón del paso 4 (UxlFOkTh punto 2: «Crear 21 clientes» cuando los 21 existían).
 * Por eso puede diferir del «Continuar con N clientes» del paso 3, que cuenta los elegidos.
 */
export function planQuotaUsage({decisions, candidates, quota}) {
    if (!quota || typeof quota.available !== 'number') return null;
    const byPk = accountsByPk(candidates);
    let needed = 0;
    const clients = new Set();
    Object.keys(decisions?.byAccount || {}).forEach(pk => {
        const decision = decisions.byAccount[pk];
        const account = byPk[pk];
        // El mismo filtro que decide qué viaja en el POST (`buildAddBulkPayload`).
        if (!decision?.include || decision.dismissed || !decision.clientRef || !account) return;
        if (decision.clientRef.type === 'new') clients.add(decision.clientRef.name);
        if (account.covered) return;
        const linkedTo = account.agency_client?.id;
        if (decision.clientRef.type === 'existing' && linkedTo !== undefined && linkedTo !== null
            && String(linkedTo) === String(decision.clientRef.id)) return;
        needed += 1;
    });
    return {limit: quota.limit, used: quota.used, available: quota.available, needed,
            clients: clients.size, left: Math.max(0, quota.available - needed),
            over: Math.max(0, needed - quota.available)};
}
